import { useState, useEffect } from 'react';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit2, Trash2, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLOR_PRESETS = [
  { name: 'Purple', value: '#8b5cf6' },
  { name: 'Green', value: '#10b981' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Orange', value: '#f59e0b' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Stone', value: '#78716c' },
  { name: 'Slate', value: '#64748b' },
];

export default function VenueTypesPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const [venueTypes, setVenueTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedVenueType, setSelectedVenueType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    color: '#8b5cf6',
    description: '',
    is_active: true
  });

  useEffect(() => {
    fetchVenueTypes();
  }, []);

  const fetchVenueTypes = async () => {
    try {
      const response = await axios.get(`${API}/venue-types`);
      setVenueTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch venue types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a venue type name');
      return;
    }
    try {
      await axios.post(`${API}/venue-types`, formData);
      toast.success('Venue type created successfully');
      setShowDialog(false);
      resetForm();
      fetchVenueTypes();
    } catch (error) {
      toast.error('Failed to create venue type');
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a venue type name');
      return;
    }
    try {
      await axios.put(`${API}/venue-types/${selectedVenueType.id}`, formData);
      toast.success('Venue type updated successfully');
      setShowDialog(false);
      resetForm();
      fetchVenueTypes();
    } catch (error) {
      toast.error('Failed to update venue type');
    }
  };

  const handleDelete = async (venueTypeId) => {
    if (!window.confirm('Are you sure you want to delete this venue type?')) return;
    try {
      await axios.delete(`${API}/venue-types/${venueTypeId}`);
      toast.success('Venue type deleted');
      fetchVenueTypes();
    } catch (error) {
      toast.error('Failed to delete venue type');
    }
  };

  const openEditDialog = (venueType) => {
    setSelectedVenueType(venueType);
    setFormData({
      name: venueType.name,
      color: venueType.color || '#8b5cf6',
      description: venueType.description || '',
      is_active: venueType.is_active
    });
    setEditMode(true);
    setShowDialog(true);
  };

  const openCreateDialog = () => {
    resetForm();
    setEditMode(false);
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      color: '#8b5cf6',
      description: '',
      is_active: true
    });
    setSelectedVenueType(null);
    setEditMode(false);
  };

  return (
    <div className="flex min-h-screen bg-obsidian" data-testid="venue-types-page">
      <Sidebar user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} />
      
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="font-heading text-4xl font-bold mb-2" data-testid="venue-types-heading">Venue Types</h1>
              <p className="text-muted-foreground">Manage your venue categories</p>
            </div>
            <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button 
                  onClick={openCreateDialog}
                  className="bg-neon-purple hover:bg-neon-purple/80 text-white" 
                  data-testid="create-venue-type-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Venue Type
                </Button>
              </DialogTrigger>
              <DialogContent className="glass-card border-glass-border max-w-lg" data-testid="venue-type-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">
                    {editMode ? 'Edit Venue Type' : 'Create Venue Type'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-muted/20 border-glass-border"
                      placeholder="e.g., Ballroom, Outdoor, Beach"
                      data-testid="venue-type-name-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Color</Label>
                    <div className="flex flex-wrap gap-2">
                      {COLOR_PRESETS.map((preset) => (
                        <button
                          key={preset.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, color: preset.value })}
                          className={`w-8 h-8 rounded-full border-2 transition-all ${
                            formData.color === preset.value 
                              ? 'border-white scale-110' 
                              : 'border-transparent hover:scale-105'
                          }`}
                          style={{ backgroundColor: preset.value }}
                          title={preset.name}
                          data-testid={`color-${preset.name.toLowerCase()}`}
                        />
                      ))}
                      <div className="flex items-center gap-2 ml-2">
                        <Input
                          type="color"
                          value={formData.color}
                          onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                          className="w-10 h-8 p-0 border-0 cursor-pointer"
                          data-testid="venue-type-color-picker"
                        />
                        <span className="text-xs text-muted-foreground font-mono">{formData.color}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Description (Optional)</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="bg-muted/20 border-glass-border"
                      placeholder="Brief description of this venue type"
                      data-testid="venue-type-description-input"
                    />
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setShowDialog(false)}
                      className="flex-1 border-glass-border"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={editMode ? handleUpdate : handleCreate}
                      className="flex-1 bg-neon-purple hover:bg-neon-purple/80 text-white"
                      data-testid="save-venue-type-btn"
                    >
                      {editMode ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : venueTypes.length === 0 ? (
            <div className="text-center py-12 glass-card rounded-3xl" data-testid="no-venue-types-message">
              <MapPin className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading text-xl font-semibold mb-2">No Venue Types</h3>
              <p className="text-muted-foreground mb-4">Create your first venue type to categorize venues</p>
              <Button onClick={openCreateDialog} className="bg-neon-purple hover:bg-neon-purple/80 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Create Venue Type
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="venue-types-list">
              {venueTypes.map((venueType) => (
                <div
                  key={venueType.id}
                  className="glass-card-hover rounded-2xl overflow-hidden"
                  data-testid={`venue-type-item-${venueType.id}`}
                >
                  <div 
                    className="h-24 w-full flex items-center justify-center"
                    style={{ backgroundColor: `${venueType.color || '#8b5cf6'}20` }}
                  >
                    <MapPin className="w-10 h-10" style={{ color: venueType.color || '#8b5cf6' }} />
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: venueType.color || '#8b5cf6' }}
                        />
                        <h3 className="font-heading text-lg font-semibold">{venueType.name}</h3>
                      </div>
                      {!venueType.is_active && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-gray-500/20 text-gray-400">
                          Inactive
                        </span>
                      )}
                    </div>
                    {venueType.description && (
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{venueType.description}</p>
                    )}
                    <div className="flex gap-2 pt-3 border-t border-glass-border">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(venueType)}
                        className="text-muted-foreground hover:text-foreground flex-1"
                        data-testid={`edit-venue-type-${venueType.id}`}
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(venueType.id)}
                        className="text-red-500 hover:text-red-400 flex-1"
                        data-testid={`delete-venue-type-${venueType.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
